import { MicroBadge } from 'components/MicroBadge';
import { LengthStep, VendorFactor } from './types';
import { Icon, type IconProp } from 'components/Icon';
import { factorKindIcons } from './const';

import { Box, PopoverPopup, PopoverRoot, PopoverTrigger, Stack } from '@ff-it/ui';
import { months } from 'options';
import type { RateChartData } from './RateChart';
import { lazy, Suspense, type ReactElement, type ReactNode } from 'react';
import { compactBadge, compactSet, moreBadge } from './FactorRateBadge.css';

type FactorPopoverProps = { content: ReactNode; children: ReactElement; popoverKey: string };
type FactorPopoverRenderer = (props: FactorPopoverProps) => ReactNode;

function FactorPopover({
  content,
  children,
  portal,
  renderPopover,
  popoverKey,
}: FactorPopoverProps & { portal?: true | string; renderPopover?: FactorPopoverRenderer }): ReactNode {
  if (renderPopover) {
    return renderPopover({ content, children, popoverKey });
  }

  return (
    <PopoverRoot>
      <PopoverTrigger openOnHover>{children}</PopoverTrigger>
      <PopoverPopup portal={portal}>{content}</PopoverPopup>
    </PopoverRoot>
  );
}

const LazyRateChart = lazy(() => import('./RateChart'));

// Fix suspense bubbling and plan remount
function RateChart(props: { data: RateChartData }): ReactNode {
  return (
    <Suspense fallback={<Box style={{ width: 280, height: 120 }} />}>
      <LazyRateChart {...props} />
    </Suspense>
  );
}

function FactorPopoverHeader({ name, description }: { name: string; description: string }) {
  return (
    <Box p="3" pb={description ? '1' : '3'} fontSize="xs" fontWeight="semibold">
      {name}
      {description && (
        <Box color="gray500" mt="1">
          {description}
        </Box>
      )}
    </Box>
  );
}

function FactorBadgePopover({
  factor,
  chart,
  portal,
  renderPopover,
  children,
}: {
  factor: VendorFactor;
  chart?: ReactNode;
  portal?: true | string;
  renderPopover?: FactorPopoverRenderer;
  children: ReactElement;
}) {
  return (
    <FactorPopover
      content={
        <>
          <FactorPopoverHeader name={factor.name} description={factor.description} />
          {chart}
        </>
      }
      popoverKey={`${factor.id}`}
      portal={portal}
      renderPopover={renderPopover}
    >
      {children}
    </FactorPopover>
  );
}

const createMonthData = (rates: Record<string, string>): RateChartData =>
  months.map((label, idx) => {
    const key = `_${idx + 1}`;
    return { label: label.substring(0, 3), value: key in rates ? Number.parseFloat(rates[key]) : 0 };
  });

const createWeekData = (rates: Record<string, string>): RateChartData =>
  Array.from(Array(52).keys()).map((_, idx) => {
    const key = `_${idx + 1}`;
    return { label: `${idx + 1}`, value: key in rates ? Number.parseFloat(rates[key]) : 0 };
  });

const createLengthData = (steps: LengthStep[]): RateChartData =>
  steps.map((step, idx) => ({
    label: `${idx === 0 ? 0 : steps[idx - 1].max_seconds + 1}-${step.max_seconds}`,
    value: Number.parseFloat(step.rate),
  }));

type BadgeConfig = { icon: IconProp; label: string; chart?: ReactNode };

function getBadgeConfig(factor: VendorFactor, symbol: string): BadgeConfig {
  const { kind } = factor;
  const icon = factorKindIcons[kind];
  switch (kind) {
    case 'CONSTANT':
      return { icon, label: `${factor.rate}${symbol}` };
    case 'MONTHLY':
    case 'WEEKLY': {
      const values = Object.values(factor.rates).map(Number.parseFloat);
      const min = Math.min(...values);
      const max = Math.max(...values);
      return {
        icon,
        label: `${min}-${max}${symbol}`,
        chart: <RateChart data={kind === 'MONTHLY' ? createMonthData(factor.rates) : createWeekData(factor.rates)} />,
      };
    }
    case 'LENGTH': {
      const steps = factor.length_steps ?? [];
      if (steps.length === 0) return { icon, label: '—' };
      const { rate: first } = steps[0];
      const { rate: last } = steps[steps.length - 1];
      return {
        icon,
        label: `${first}${symbol}${steps.length > 1 ? `-${last}${symbol}` : ''}`,
        chart: <RateChart data={createLengthData(steps)} />,
      };
    }
    default:
      throw new Error(`Unknown kind ${kind}`);
  }
}

export function FactorRateBadge({
  factor,
  portal = undefined,
  renderPopover,
}: {
  factor: VendorFactor;
  portal?: true | string;
  renderPopover?: FactorPopoverRenderer;
}) {
  const symbol = factor.form === 'PERCENTAGE' ? '%' : '';
  const { icon, label, chart } = getBadgeConfig(factor, symbol);

  return (
    <MicroBadge>
      <FactorBadgePopover factor={factor} chart={chart} portal={portal} renderPopover={renderPopover}>
        <span style={{ cursor: 'pointer' }}>
          <Icon icon={icon} /> {label}
        </span>
      </FactorBadgePopover>
    </MicroBadge>
  );
}

// Drops the icon for CONSTANT (keeping the bare rate) and the range for temporal/length kinds,
// leaving just the kind icon. The dropped detail lives in the shared popover chart.
function getCompactContent(factor: VendorFactor, symbol: string): ReactNode {
  const { kind } = factor;
  switch (kind) {
    case 'CONSTANT':
      return `${factor.rate}${symbol}`;
    case 'MONTHLY':
    case 'WEEKLY':
    case 'LENGTH':
      return <Icon icon={factorKindIcons[kind]} />;
    default:
      throw new Error(`Unknown kind ${kind}`);
  }
}

const MAX_VISIBLE = 3;

export function FactorRateBadgeSet({
  factors,
  portal = undefined,
  renderPopover,
}: {
  factors: VendorFactor[];
  portal?: true | string;
  renderPopover?: FactorPopoverRenderer;
}) {
  if (factors.length === 0) {
    return null;
  }

  // Leave a slot for the overflow indicator when there are more than we can show.
  const visible = factors.length > MAX_VISIBLE ? factors.slice(0, MAX_VISIBLE - 1) : factors;
  const overflow = factors.length - visible.length;

  const content = (
    <Stack direction="vertical">
      {factors.map((factor, idx) => {
        const symbol = factor.form === 'PERCENTAGE' ? '%' : '';
        const { label, chart } = getBadgeConfig(factor, symbol);
        return (
          <Box key={factor.id} borderTop={idx === 0 ? undefined : 'subtle'}>
            <FactorPopoverHeader name={factor.name} description={factor.description} />
            {chart ?? (
              <Box px="3" pb="3" fontSize="xs" fontWeight="medium">
                {label}
              </Box>
            )}
          </Box>
        );
      })}
    </Stack>
  );
  const children = (
    <div className={compactSet}>
      {visible.map((factor) => {
        const symbol = factor.form === 'PERCENTAGE' ? '%' : '';
        return (
          <span key={factor.id} className={compactBadge} data-testid={`factor-compact-badge-${factor.id}`}>
            {getCompactContent(factor, symbol)}
          </span>
        );
      })}
      {overflow > 0 && <span className={moreBadge}>+{overflow}</span>}
    </div>
  );

  return (
    <FactorPopover
      content={content}
      popoverKey={factors.map(({ id }) => id).join(',')}
      portal={portal}
      renderPopover={renderPopover}
    >
      {children}
    </FactorPopover>
  );
}
